/**
 * JSON Merge Patch (RFC 7396), the shape `PATCH users/settings` takes (API-617): objects merge key
 * by key, `null` removes a key at any depth, and an array or a scalar replaces what was there
 * whole. Pure, no browser access.
 *
 * One consequence to keep in mind when storing a value this way: `null` cannot be a stored value,
 * because in a patch it means "remove". Settings therefore never hold `null`; a missing key is
 * what a feature's parse-with-defaults function turns into its default.
 */

export type JsonRecord = Record<string, unknown>;

export function isPlainObject(value: unknown): value is JsonRecord {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** Structural equality for JSON values. A key holding `undefined` counts as absent. */
export function jsonEquals(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (Array.isArray(a) || Array.isArray(b)) {
        if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
        return a.every((item, index) => jsonEquals(item, b[index]));
    }
    if (!isPlainObject(a) || !isPlainObject(b)) return false;
    const aKeys = Object.keys(a).filter(key => a[key] !== undefined);
    const bKeys = Object.keys(b).filter(key => b[key] !== undefined);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every(key => b[key] !== undefined && jsonEquals(a[key], b[key]));
}

/**
 * What the server does with a patch. Never mutates `target`; an untouched branch of the result is
 * the same object as in `target`, so a caller can tell by identity what did not change.
 */
export function applyMergePatch(target: unknown, patch: unknown): unknown {
    if (!isPlainObject(patch)) return patch;

    const base = isPlainObject(target) ? target : {};
    let result: JsonRecord | undefined;
    for (const [key, value] of Object.entries(patch)) {
        if (value === undefined) continue;
        if (value === null) {
            if (key in base) {
                result ??= { ...base };
                delete result[key];
            }
            continue;
        }
        const next = applyMergePatch(base[key], value);
        if (next !== base[key]) {
            result ??= { ...base };
            result[key] = next;
        }
    }
    if (result) return result;
    return isPlainObject(target) ? target : base;
}

/**
 * The smallest patch that turns `from` into `to`, or `undefined` when they are already equal.
 * Only changed keys appear; a key that `to` no longer has becomes `null`; an array that changed at
 * all is sent complete, because the server replaces arrays whole.
 */
export function diffMergePatch(from: unknown, to: unknown): unknown {
    if (to === undefined || to === null) {
        return from === undefined || from === null ? undefined : null;
    }
    if (!isPlainObject(from) || !isPlainObject(to)) {
        return jsonEquals(from, to) ? undefined : to;
    }

    const patch: JsonRecord = {};
    for (const key of Object.keys(from)) {
        if (from[key] !== undefined && (to[key] === undefined || to[key] === null)) {
            patch[key] = null;
        }
    }
    for (const [key, value] of Object.entries(to)) {
        if (value === undefined || value === null) continue;
        const child = diffMergePatch(from[key], value);
        if (child !== undefined) patch[key] = child;
    }
    return Object.keys(patch).length > 0 ? patch : undefined;
}

/**
 * One patch equivalent to applying `first` and then `second`, for folding writes made within one
 * debounce window into a single request.
 *
 * Exact except in one corner: a key that `first` removes or replaces with a non-object and that
 * `second` then sets to an object comes out as that object, which the server MERGES into whatever
 * it still holds there instead of replacing it. A stale sibling key can survive that. It is
 * harmless for settings, whose readers ignore keys they do not know.
 */
export function composeMergePatches(first: unknown, second: unknown): unknown {
    if (second === undefined) return first;
    if (!isPlainObject(first) || !isPlainObject(second)) return second;

    const result: JsonRecord = { ...first };
    for (const [key, value] of Object.entries(second)) {
        if (value === undefined) continue;
        result[key] = key in first ? composeMergePatches(first[key], value) : value;
    }
    return result;
}

/** The value at `path`, or `undefined` when any step is missing or not an object. */
export function getAtPath(document: unknown, path: readonly string[]): unknown {
    let current = document;
    for (const key of path) {
        if (!isPlainObject(current)) return undefined;
        current = current[key];
    }
    return current;
}

/** `value` nested under `path`: `(['a', 'b'], 1)` is `{ a: { b: 1 } }`. */
export function wrapAtPath(path: readonly string[], value: unknown): unknown {
    return path.reduceRight<unknown>((inner, key) => ({ [key]: inner }), value);
}
